import ExcelJS from 'exceljs'
import type {
  TimesheetEmployee,
  TimesheetDay,
} from '../types/reports.types'

const WEEKDAY_NAMES = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']

const COLORS = {
  yellow: 'FEF9C3',     // 1-15 мин
  orange: 'FFEDD5',     // 16-30 мин
  red: 'FEE2E2',        // 31+ мин
  redAbsent: 'FEF2F2',  // неявка
  gray: 'F3F4F6',       // выходной
  orangeHeader: 'FFF7ED',
  redHeader: 'FEF2F2',
  blueHeader: 'DBEAFE',
  greenHeader: 'DCFCE7',
  redText: 'DC2626',
  orangeText: 'C2410C',
  weekendText: 'EF4444',
  orgHeader: 'E2E8F0',
}

interface OrganizationGroup {
  organizationName: string
  employees: TimesheetEmployee[]
}

type CellStyle = 'normal' | 'mildLate' | 'moderateLate' | 'severeLate' | 'absent' | 'weekend' | 'orgHeader'

function getLatenessCellStyle(day: TimesheetDay | undefined): CellStyle {
  if (!day) return 'normal'
  if (!day.is_working_day) return 'weekend'
  if (!day.check_in && day.scheduled_start) return 'absent'
  if (!day.late_minutes || day.late_minutes <= 0) return 'normal'
  if (day.late_minutes <= 15) return 'mildLate'
  if (day.late_minutes <= 30) return 'moderateLate'
  return 'severeLate'
}

function formatLateMinutes(day: TimesheetDay | undefined): string {
  if (!day) return ''
  if (!day.is_working_day) return 'В'
  if (!day.check_in && day.scheduled_start) return 'Н'
  if (!day.late_minutes || day.late_minutes <= 0) return ''
  return String(day.late_minutes)
}

function formatMinutesTotal(minutes: number): string {
  if (minutes <= 0) return '0'
  if (minutes < 60) return String(minutes)
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  if (mins === 0) return `${hours}ч`
  return `${hours}ч ${mins}м`
}

// Десятичные часы: минуты / 60 (60 мин → 1, 1397 мин (23ч17м) → 23.2833)
function minutesToDecimal(minutes: number): number {
  if (minutes <= 0) return 0
  return Math.round((minutes / 60) * 10000) / 10000
}

function groupByOrganization(employees: TimesheetEmployee[]): OrganizationGroup[] {
  const groups = new Map<string | null, TimesheetEmployee[]>()
  for (const emp of employees) {
    const key = emp.organization_id
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key)!.push(emp)
  }
  return Array.from(groups.entries()).map(([, emps]) => ({
    organizationName: emps[0]?.organization_name || 'Без организации',
    employees: emps,
  }))
}

function generateDates(year: number, month: number, daysInMonth: number): Date[] {
  const result: Date[] = []
  for (let day = 1; day <= daysInMonth; day++) {
    result.push(new Date(year, month - 1, day))
  }
  return result
}

function formatDateString(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function isWeekend(date: Date): boolean {
  const day = date.getDay()
  return day === 0 || day === 6
}

function applyLatenessCellStyle(cell: ExcelJS.Cell, style: CellStyle): void {
  cell.alignment = { horizontal: 'center', vertical: 'middle' }
  cell.border = {
    top: { style: 'thin', color: { argb: 'D1D5DB' } },
    left: { style: 'thin', color: { argb: 'D1D5DB' } },
    bottom: { style: 'thin', color: { argb: 'D1D5DB' } },
    right: { style: 'thin', color: { argb: 'D1D5DB' } },
  }

  switch (style) {
    case 'mildLate':
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.yellow } }
      break
    case 'moderateLate':
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.orange } }
      cell.font = { color: { argb: COLORS.orangeText } }
      break
    case 'severeLate':
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.red } }
      cell.font = { color: { argb: COLORS.redText }, bold: true }
      break
    case 'absent':
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.redAbsent } }
      cell.font = { color: { argb: COLORS.redText }, bold: true }
      break
    case 'weekend':
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.gray } }
      break
    case 'orgHeader':
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.orgHeader } }
      cell.font = { bold: true }
      break
    case 'normal':
      // no special styling
      break
  }
}

export async function exportLatenessToExcel(
  employees: TimesheetEmployee[],
  meta: { year: number; month: number; days_in_month: number; month_name: string },
): Promise<void> {
  const dates = generateDates(meta.year, meta.month, meta.days_in_month)
  const dateStrings = dates.map(formatDateString)
  const organizationGroups = groupByOrganization(employees)
  const hasMultipleOrgs = organizationGroups.length > 1

  const workbook = new ExcelJS.Workbook()
  workbook.creator = 'TCO Admin'
  workbook.created = new Date()

  const worksheet = workbook.addWorksheet('Опоздания', {
    views: [{ state: 'frozen', xSplit: 1, ySplit: 2 }],
  })

  // 1 (name) + days + 7 (summary: late_minutes, late_minutes_decimal, late_count, salary, hourly_rate, cost_per_minute, deduction)
  const totalColumns = 1 + dates.length + 7

  // Title row
  const titleRow = worksheet.addRow([`Отчёт по опозданиям — ${meta.month_name} ${meta.year}`])
  worksheet.mergeCells(1, 1, 1, Math.min(15, totalColumns))
  titleRow.font = { size: 14, bold: true }
  titleRow.height = 25

  // Header row 1: Weekday names
  const headerRow1Data: string[] = ['Сотрудник']
  for (const date of dates) {
    headerRow1Data.push(WEEKDAY_NAMES[date.getDay()])
  }
  headerRow1Data.push('Опозд. (мин)', 'Опозд. (число)', 'Кол-во', 'Оклад', 'Час. ставка', 'Стоим. 1 мин', 'Сумма удерж.')
  const headerRow1 = worksheet.addRow(headerRow1Data)
  headerRow1.font = { bold: true, size: 9 }
  headerRow1.height = 20

  headerRow1.eachCell((cell, colNumber) => {
    cell.alignment = { horizontal: 'center', vertical: 'middle' }
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    }
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F3F4F6' } }

    if (colNumber > 1 && colNumber <= dates.length + 1) {
      const dateIndex = colNumber - 2
      if (isWeekend(dates[dateIndex])) {
        cell.font = { bold: true, size: 9, color: { argb: COLORS.weekendText } }
      }
    }
    if (colNumber === totalColumns - 6 || colNumber === totalColumns - 5) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.orangeHeader } }
    } else if (colNumber === totalColumns - 4) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.redHeader } }
    } else if (
      colNumber === totalColumns - 3 ||
      colNumber === totalColumns - 2 ||
      colNumber === totalColumns - 1
    ) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.blueHeader } }
    } else if (colNumber === totalColumns) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.greenHeader } }
    }
  })

  // Header row 2: Day numbers
  const headerRow2Data: (string | number)[] = ['Подразделение']
  for (const date of dates) {
    headerRow2Data.push(date.getDate())
  }
  headerRow2Data.push('', '', '', '', '', '', '')
  const headerRow2 = worksheet.addRow(headerRow2Data)
  headerRow2.font = { bold: true, size: 9 }
  headerRow2.height = 18

  headerRow2.eachCell((cell, colNumber) => {
    cell.alignment = { horizontal: 'center', vertical: 'middle' }
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    }
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F3F4F6' } }

    if (colNumber > 1 && colNumber <= dates.length + 1) {
      const dateIndex = colNumber - 2
      if (isWeekend(dates[dateIndex])) {
        cell.font = { bold: true, size: 9, color: { argb: COLORS.weekendText } }
      }
    }
    if (colNumber === totalColumns - 6 || colNumber === totalColumns - 5) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.orangeHeader } }
    } else if (colNumber === totalColumns - 4) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.redHeader } }
    } else if (
      colNumber === totalColumns - 3 ||
      colNumber === totalColumns - 2 ||
      colNumber === totalColumns - 1
    ) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.blueHeader } }
    } else if (colNumber === totalColumns) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.greenHeader } }
    }
  })

  // Data rows
  for (const group of organizationGroups) {
    if (hasMultipleOrgs) {
      const orgRowData: (string | number)[] = [group.organizationName]
      for (let i = 0; i < dates.length + 6; i++) orgRowData.push('')
      const orgRow = worksheet.addRow(orgRowData)
      worksheet.mergeCells(orgRow.number, 1, orgRow.number, totalColumns)
      orgRow.eachCell((cell) => applyLatenessCellStyle(cell, 'orgHeader'))
      orgRow.height = 22
    }

    for (const employee of group.employees) {
      const rowData: (string | number)[] = []

      const nameWithDept = employee.department_name
        ? `${employee.employee_name} (${employee.department_name})`
        : employee.employee_name
      rowData.push(nameWithDept)

      for (const dateStr of dateStrings) {
        const day = employee.days[dateStr]
        rowData.push(formatLateMinutes(day))
      }

      rowData.push(formatMinutesTotal(employee.totals.late_minutes_total))
      rowData.push(
        employee.totals.late_minutes_total > 0
          ? minutesToDecimal(employee.totals.late_minutes_total)
          : '',
      )
      rowData.push(employee.totals.late_count)
      rowData.push(employee.totals.salary != null ? employee.totals.salary : '')
      rowData.push(employee.totals.hourly_rate != null ? employee.totals.hourly_rate : '')
      rowData.push(employee.totals.cost_per_minute != null ? employee.totals.cost_per_minute : '')
      rowData.push(
        employee.totals.deduction_amount != null && employee.totals.deduction_amount > 0
          ? employee.totals.deduction_amount
          : '',
      )

      const row = worksheet.addRow(rowData)
      row.height = 20

      row.eachCell((cell, colNumber) => {
        cell.border = {
          top: { style: 'thin', color: { argb: 'D1D5DB' } },
          left: { style: 'thin', color: { argb: 'D1D5DB' } },
          bottom: { style: 'thin', color: { argb: 'D1D5DB' } },
          right: { style: 'thin', color: { argb: 'D1D5DB' } },
        }

        if (colNumber === 1) {
          cell.alignment = { horizontal: 'left', vertical: 'middle' }
          cell.font = { size: 10 }
        } else if (colNumber > 1 && colNumber <= dates.length + 1) {
          const dateIndex = colNumber - 2
          const dateStr = dateStrings[dateIndex]
          const day = employee.days[dateStr]
          const style = getLatenessCellStyle(day)
          applyLatenessCellStyle(cell, style)
          cell.font = { size: 9, ...cell.font }
        } else if (colNumber === totalColumns - 6 || colNumber === totalColumns - 5) {
          cell.alignment = { horizontal: 'center', vertical: 'middle' }
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.orangeHeader } }
          if (employee.totals.late_minutes_total > 0) {
            cell.font = { size: 10, bold: true, color: { argb: COLORS.orangeText } }
          } else {
            cell.font = { size: 10 }
          }
        } else if (colNumber === totalColumns - 4) {
          cell.alignment = { horizontal: 'center', vertical: 'middle' }
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.redHeader } }
          if (employee.totals.late_count > 0) {
            cell.font = { size: 10, bold: true, color: { argb: COLORS.redText } }
          } else {
            cell.font = { size: 10 }
          }
        } else if (
          colNumber === totalColumns - 3 ||
          colNumber === totalColumns - 2 ||
          colNumber === totalColumns - 1
        ) {
          cell.alignment = { horizontal: 'center', vertical: 'middle' }
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.blueHeader } }
          cell.font = { size: 10 }
        } else if (colNumber === totalColumns) {
          cell.alignment = { horizontal: 'center', vertical: 'middle' }
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.greenHeader } }
          if (employee.totals.deduction_amount != null && employee.totals.deduction_amount > 0) {
            cell.font = { size: 10, bold: true, color: { argb: '15803D' } }
          } else {
            cell.font = { size: 10 }
          }
        }
      })
    }
  }

  // Totals row
  const totalLateMinutes = employees.reduce((sum, emp) => sum + emp.totals.late_minutes_total, 0)
  const totalLateCount = employees.reduce((sum, emp) => sum + emp.totals.late_count, 0)

  const totalsRowData: (string | number)[] = ['ИТОГО']
  for (let i = 0; i < dates.length; i++) totalsRowData.push('')
  totalsRowData.push(formatMinutesTotal(totalLateMinutes))
  totalsRowData.push(totalLateMinutes > 0 ? minutesToDecimal(totalLateMinutes) : '')
  totalsRowData.push(totalLateCount)
  totalsRowData.push('') // salary — no total
  totalsRowData.push('') // hourly_rate — no total
  totalsRowData.push('') // cost_per_minute — no total
  const totalDeduction = employees.reduce((sum, emp) => sum + (emp.totals.deduction_amount || 0), 0)
  totalsRowData.push(totalDeduction > 0 ? Math.round(totalDeduction) : '')

  const totalsRow = worksheet.addRow(totalsRowData)
  totalsRow.height = 22
  totalsRow.font = { bold: true, size: 11 }

  totalsRow.eachCell((cell, colNumber) => {
    cell.border = {
      top: { style: 'medium' },
      left: { style: 'thin' },
      bottom: { style: 'medium' },
      right: { style: 'thin' },
    }
    cell.alignment = { horizontal: colNumber === 1 ? 'left' : 'center', vertical: 'middle' }

    if (colNumber === totalColumns - 6 || colNumber === totalColumns - 5) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.orangeHeader } }
    } else if (colNumber === totalColumns - 4) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.redHeader } }
    } else if (
      colNumber === totalColumns - 3 ||
      colNumber === totalColumns - 2 ||
      colNumber === totalColumns - 1
    ) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.blueHeader } }
    } else if (colNumber === totalColumns) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.greenHeader } }
    }
  })

  // Column widths
  worksheet.getColumn(1).width = 35
  for (let i = 2; i <= dates.length + 1; i++) {
    worksheet.getColumn(i).width = 5
  }
  worksheet.getColumn(dates.length + 2).width = 14 // late_minutes
  worksheet.getColumn(dates.length + 3).width = 12 // late_minutes (decimal)
  worksheet.getColumn(dates.length + 4).width = 10 // late_count
  worksheet.getColumn(dates.length + 5).width = 14 // salary
  worksheet.getColumn(dates.length + 6).width = 14 // hourly_rate
  worksheet.getColumn(dates.length + 7).width = 14 // cost_per_minute
  worksheet.getColumn(dates.length + 8).width = 16 // deduction

  // Legend
  worksheet.addRow([])
  const legendRowNum = worksheet.rowCount

  const legendItems = [
    { text: '1-15 мин', color: COLORS.yellow },
    { text: '16-30 мин', color: COLORS.orange },
    { text: '31+ мин', color: COLORS.red },
    { text: 'Неявка', color: COLORS.redAbsent },
    { text: 'Выходной', color: COLORS.gray },
  ]

  let legendCol = 1
  for (const item of legendItems) {
    const cell = worksheet.getCell(legendRowNum + 1, legendCol)
    cell.value = item.text
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: item.color } }
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    }
    cell.alignment = { horizontal: 'center' }
    cell.font = { size: 9 }
    legendCol += 2
  }

  // Generate and download
  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })

  const filename = `Опоздания_${meta.month_name}_${meta.year}.xlsx`
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  link.click()
  URL.revokeObjectURL(link.href)
}
